import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Mail } from "lucide-react";

import AuthLayout from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/auth/AuthContext";
import { safeNext } from "@/auth/loginUrl";
import { errorCode, errorMessage } from "@/lib/errors";

export default function Login() {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  if (status === "authed") return <Navigate to={next} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate(next, { replace: true });
    } catch (err) {
      const code = errorCode(err);
      if (code === "PASSWORD_SETUP_REQUIRED") {
        setNotice("Your account was moved to the new EventPass. We emailed you a link to set your password.");
      } else if (code === "RATE_LIMITED") {
        setError(errorMessage(err));
      } else {
        setError(errorMessage(err, "That email and password do not match."));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to buy tickets and see your orders"
      footer={
        <>
          New here?{" "}
          <Link to={`/SignUp?next=${encodeURIComponent(next)}`} className="text-indigo-600 font-medium hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        {notice && (
          <div className="flex items-start gap-2 bg-indigo-50 border border-indigo-100 text-indigo-800 text-sm rounded-lg px-3 py-2">
            <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" /> <span>{notice}</span>
          </div>
        )}
        {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/ForgotPassword" className="text-xs text-indigo-600 hover:underline">
              Forgot password?
            </Link>
          </div>
          <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </div>
        <Button type="submit" disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-700">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
        </Button>
      </form>
    </AuthLayout>
  );
}
